/**
 * Decision Health
 *
 * Computes a health status for a Decision record based on:
 *   - Whether rationale fields are populated (orphaned if none are)
 *   - Whether the review date has passed (stale) or is approaching (aging)
 *
 * Health states:
 *   fresh    — rationale is documented and review date is not approaching
 *   aging    — review date is within 14 days, or rationale is partial
 *   stale    — review date has passed, or rationale context + reasoning both missing but other fields present
 *   orphaned — a manual decision with no rationale at all: no rationale fields and nothing written in its body
 */

import type { Decision, DecisionHealthStatus } from '../types/prioritization/decisions';
import { computeHealthStatus } from '../../supabase/functions/_shared/decisionHealthCore.ts';

// The rule itself lives in one place, shared with the public API
// (GET /v1/decisions?health=): supabase/functions/_shared/decisionHealthCore.ts.
export { computeHealthStatus };

// ── UI helpers ───────────────────────────────────────────────────────────────

export const HEALTH_CONFIG: Record<DecisionHealthStatus, {
  label: string;
  color: string;
  dot: string;
  description: string;
}> = {
  fresh: {
    label: 'Fresh',
    color: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    dot:   'bg-[var(--green)]',
    description: 'Rationale is documented and current.',
  },
  aging: {
    label: 'Aging',
    color: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    dot:   'bg-[var(--mustard)]',
    description: 'Approaching review date or rationale is incomplete.',
  },
  stale: {
    label: 'Stale',
    color: 'bg-[var(--coral-dim)] text-[var(--coral)]',
    dot:   'bg-[var(--coral)]',
    description: 'Past review date — rationale should be revisited.',
  },
  orphaned: {
    label: 'Orphaned',
    color: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
    dot:   'bg-[var(--surface)]',
    description: 'No rationale documented for this decision.',
  },
};

export function getHealthLabel(status: DecisionHealthStatus | undefined): string {
  if (!status) return '';
  return HEALTH_CONFIG[status]?.label ?? '';
}

// ── Aggregate metrics ────────────────────────────────────────────────────────

export interface DecisionHealthSummary {
  total: number;
  fresh: number;
  aging: number;
  stale: number;
  orphaned: number;
  coverageScore: number; // 0–100: percentage with at least basic rationale
}

export function summarizeHealth(decisions: Decision[]): DecisionHealthSummary {
  const total = decisions.length;
  if (total === 0) {
    return { total: 0, fresh: 0, aging: 0, stale: 0, orphaned: 0, coverageScore: 0 };
  }

  const counts = { fresh: 0, aging: 0, stale: 0, orphaned: 0 };
  for (const d of decisions) {
    const h = d.healthStatus ?? computeHealthStatus(d);
    counts[h]++;
  }

  const documented = total - counts.orphaned;
  const coverageScore = Math.round((documented / total) * 100);

  return { total, ...counts, coverageScore };
}
